import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Form, Input } from 'antd';
import type { FormInstance } from 'antd';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import {
  AUTH_FORM_PROPS,
  AuthLayout,
  AuthSuccessBadge,
  AuthSwitch,
  authStyles,
  codeRules,
  emailRules,
  newPasswordRules,
  normalizeDigits,
  normalizeEmail,
  passwordIconRender,
  setFieldQuietly,
} from 'layouts/auth';
import { paths } from 'routes/routes';
import {
  FORGOT_MESSAGES,
  isCodeWrong,
  mapSendCodeError,
  mapStepError,
  requestResetCode,
  resetPassword,
  verifyResetCode,
} from './forgot-api';
import styles from './styles.module.scss';

/**
 * /forgot-password — wizard 3 bước (đặc tả §3.3): email → mã 4 số → mật khẩu mới → hoàn tất.
 * Email và mã chỉ nằm trong state của component (không lên URL): tải lại trang là về bước 1.
 */

type Step = 'email' | 'code' | 'password' | 'done';

const RESEND_COOLDOWN_MS = 60_000;
const TOTAL_STEPS = 3;

// Chỉ tự đặt con trỏ khi màn rộng: trên điện thoại sẽ bật bàn phím che mất nửa trang.
const isWideScreen = () => {
  try {
    return window.matchMedia('(min-width: 900px)').matches;
  } catch {
    return false;
  }
};

/** Đang có lỗi thì kiểm tra lại ngay khi gõ, để lỗi biến mất lúc người dùng sửa xong. */
const revalidateIfInvalid = (form: FormInstance, changed: Record<string, unknown>) => {
  const invalid = Object.keys(changed).filter((name) => form.getFieldError(name).length > 0);
  if (invalid.length) form.validateFields(invalid).catch(() => undefined);
};

/**
 * Số giây còn lại tới `target` (ms), tự đếm lùi mỗi giây. Đọc Date.now() ngay lúc render (không
 * giữ `now` trong state): đổi `target` sau khi đồng hồ đã dừng thì số hiện ra vẫn đúng ngay.
 */
const useSecondsLeft = (target: number) => {
  const [, tick] = useState(0);
  const secondsLeft = Math.max(0, Math.ceil((target - Date.now()) / 1000));
  const running = secondsLeft > 0;
  useEffect(() => {
    if (!running) return undefined;
    const id = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [running, target]);
  return secondsLeft;
};

// ───────────────────────── Bước 1: email ─────────────────────────

interface StepEmailProps {
  initialEmail: string;
  autoFocus: boolean;
  onSent: (email: string) => void;
}

const StepEmail = ({ initialEmail, autoFocus, onSent }: StepEmailProps) => {
  const [form] = Form.useForm<{ email: string }>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onFinish = async ({ email }: { email: string }) => {
    if (loading) return;
    const normalized = normalizeEmail(email);
    setError(null);
    setLoading(true);
    try {
      await requestResetCode(normalized);
      onSent(normalized);
    } catch (err) {
      setError(mapSendCodeError(err));
      setLoading(false);
    }
  };

  return (
    <Form
      {...AUTH_FORM_PROPS}
      form={form}
      name="forgot-email"
      initialValues={{ email: initialEmail }}
      onFinish={onFinish}
      onValuesChange={(changed) => {
        if (error) setError(null);
        revalidateIfInvalid(form, changed);
      }}
    >
      {error && <Alert type="error" showIcon message={error} className={authStyles.formAlert} />}

      <Form.Item
        name="email"
        label="Email"
        rules={emailRules}
        validateFirst
        validateTrigger="onBlur"
        normalize={normalizeEmail}
      >
        <Input
          type="email"
          inputMode="email"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          placeholder="Nhập email của bạn"
          prefix={<MailOutlined aria-hidden="true" />}
          autoFocus={autoFocus}
        />
      </Form.Item>

      <Button type="primary" htmlType="submit" size="large" block loading={loading}>
        {loading ? 'Đang gửi mã…' : 'Gửi mã xác nhận'}
      </Button>
    </Form>
  );
};

// ───────────────────────── Bước 2: mã 4 số ─────────────────────────

interface StepCodeProps {
  email: string;
  initialCode: string;
  /** Lỗi gắn sẵn vào ô mã (vd. bước 3 bị backend báo sai mã rồi quay lại đây). */
  initialCodeError: string | null;
  resendAt: number;
  autoFocus: boolean;
  onResent: () => void;
  onVerified: (code: string) => void;
  onChangeEmail: () => void;
}

const StepCode = ({
  email,
  initialCode,
  initialCodeError,
  resendAt,
  autoFocus,
  onResent,
  onVerified,
  onChangeEmail,
}: StepCodeProps) => {
  const [form] = Form.useForm<{ code: string }>();
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const secondsLeft = useSecondsLeft(resendAt);

  useEffect(() => {
    if (initialCodeError) form.setFields([{ name: 'code', errors: [initialCodeError] }]);
  }, [form, initialCodeError]);

  const onFinish = async ({ code }: { code: string }) => {
    if (loading || resending) return;
    setNotice(null);
    setLoading(true);
    try {
      await verifyResetCode(email, code);
      onVerified(code);
    } catch (err) {
      setLoading(false);
      if (isCodeWrong(err)) {
        form.setFields([{ name: 'code', errors: [FORGOT_MESSAGES.codeWrong] }]);
        form.getFieldInstance('code')?.focus?.();
      } else {
        setNotice({ type: 'error', text: mapStepError(err) });
      }
    }
  };

  const onResend = async () => {
    if (resending || loading || secondsLeft > 0) return;
    setNotice(null);
    setResending(true);
    try {
      await requestResetCode(email);
      onResent();
      // Mã cũ hết dùng được: xoá ô và lỗi "Mã không đúng" (nếu có) trong một lần.
      setFieldQuietly(form, 'code', '');
      setNotice({ type: 'success', text: FORGOT_MESSAGES.codeResent });
    } catch (err) {
      setNotice({ type: 'error', text: mapSendCodeError(err) });
    } finally {
      setResending(false);
    }
  };

  const resendLabel = resending
    ? 'Đang gửi lại…'
    : secondsLeft > 0
    ? `Gửi lại mã sau ${secondsLeft} giây`
    : 'Gửi lại mã';

  return (
    <Form
      {...AUTH_FORM_PROPS}
      form={form}
      name="forgot-code"
      initialValues={{ code: initialCode }}
      onFinish={onFinish}
      onValuesChange={(changed) => {
        if (notice?.type === 'error') setNotice(null);
        revalidateIfInvalid(form, changed);
      }}
    >
      {notice && (
        <Alert type={notice.type} showIcon message={notice.text} className={authStyles.formAlert} />
      )}

      <Form.Item
        name="code"
        label="Mã xác nhận (4 số)"
        rules={codeRules}
        validateFirst
        // Chỉ kiểm tra khi bấm Xác nhận (và gõ lại khi đang báo lỗi): đang gõ dở 2 số hay
        // bấm "Gửi lại mã" không bị báo lỗi.
        validateTrigger={false}
        // Dán cả câu "Mã của bạn: 1234" vẫn ra 1234. Không đặt maxLength: trình duyệt cắt
        // chuỗi dán TRƯỚC khi lọc số.
        normalize={(value?: string) => normalizeDigits(value).slice(0, 4)}
      >
        <Input
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          placeholder="Nhập 4 chữ số"
          className={styles.codeInput}
          autoFocus={autoFocus}
        />
      </Form.Item>

      <Button type="primary" htmlType="submit" size="large" block loading={loading}>
        {loading ? 'Đang kiểm tra…' : 'Xác nhận mã'}
      </Button>

      <div className={styles.codeActions}>
        {/* Khoá khi đang gọi API: phản hồi đến muộn sẽ đẩy wizard sang bước khác. */}
        <Button
          type="link"
          size="small"
          className={styles.linkButton}
          onClick={onChangeEmail}
          disabled={loading || resending}
        >
          Đổi email
        </Button>
        <Button
          type="link"
          size="small"
          className={styles.linkButton}
          onClick={onResend}
          disabled={secondsLeft > 0 || resending}
        >
          {resendLabel}
        </Button>
      </div>
    </Form>
  );
};

// ───────────────────────── Bước 3: mật khẩu mới ─────────────────────────

interface StepPasswordProps {
  email: string;
  code: string;
  autoFocus: boolean;
  onDone: () => void;
  onCodeRejected: () => void;
}

const StepPassword = ({ email, code, autoFocus, onDone, onCodeRejected }: StepPasswordProps) => {
  const [form] = Form.useForm<{ newPassword: string }>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onFinish = async ({ newPassword }: { newPassword: string }) => {
    if (loading) return;
    setError(null);
    setLoading(true);
    try {
      await resetPassword(email, code, newPassword);
      onDone();
    } catch (err) {
      setLoading(false);
      if (isCodeWrong(err)) {
        onCodeRejected();
      } else {
        setError(mapStepError(err));
      }
    }
  };

  return (
    <Form
      {...AUTH_FORM_PROPS}
      form={form}
      name="forgot-password"
      onFinish={onFinish}
      onValuesChange={(changed) => {
        if (error) setError(null);
        revalidateIfInvalid(form, changed);
      }}
    >
      {error && <Alert type="error" showIcon message={error} className={authStyles.formAlert} />}

      {/* Cho trình quản lý mật khẩu biết mật khẩu mới thuộc tài khoản nào. */}
      <input type="text" name="username" autoComplete="username" value={email} readOnly hidden />

      <Form.Item
        name="newPassword"
        label="Mật khẩu mới"
        rules={newPasswordRules}
        validateFirst
        validateTrigger="onBlur"
        extra="8–32 ký tự. Mật khẩu này dùng cho cả trang quản trị và app HP Media."
      >
        <Input.Password
          autoComplete="new-password"
          placeholder="Nhập mật khẩu mới"
          prefix={<LockOutlined aria-hidden="true" />}
          iconRender={passwordIconRender}
          autoFocus={autoFocus}
        />
      </Form.Item>

      <Button type="primary" htmlType="submit" size="large" block loading={loading}>
        {loading ? 'Đang lưu…' : 'Đổi mật khẩu'}
      </Button>
    </Form>
  );
};

// ───────────────────────── Hoàn tất ─────────────────────────

const StepDone = ({ email, autoFocus }: { email: string; autoFocus: boolean }) => {
  const navigate = useNavigate();
  return (
    <div className={styles.done}>
      <AuthSuccessBadge />
      <p className={styles.doneAccount}>
        Tài khoản <strong>{email}</strong>
      </p>
      <Button
        type="primary"
        size="large"
        block
        autoFocus={autoFocus}
        onClick={() =>
          navigate(paths.login, { replace: true, state: { email, notice: 'passwordChanged' } })
        }
      >
        Về trang đăng nhập
      </Button>
    </div>
  );
};

// ───────────────────────── Wizard ─────────────────────────

const STEP_NUMBER: Record<Exclude<Step, 'done'>, number> = { email: 1, code: 2, password: 3 };

/** H1 + phụ đề từng bước (đặc tả §2.8). Bước 2 cần email nên phụ đề là hàm. */
const STEP_COPY: Record<Step, { title: string; subtitle: (email: string) => ReactNode }> = {
  email: {
    title: 'Quên mật khẩu',
    subtitle: () =>
      'Nhập email đã đăng ký, chúng tôi sẽ gửi mã xác nhận 4 số để bạn đặt lại mật khẩu.',
  },
  code: {
    title: 'Nhập mã xác nhận',
    subtitle: (email) => (
      <>
        Nếu <strong className={styles.email}>{email}</strong> có tài khoản đã xác minh, mã 4 số đã
        được gửi tới hộp thư. Không thấy mã? Kiểm tra thư mục Spam.
      </>
    ),
  },
  password: {
    title: 'Đặt mật khẩu mới',
    subtitle: () => 'Mật khẩu mới dùng cho cả trang quản trị và app HP Media.',
  },
  done: {
    title: 'Đã đổi mật khẩu',
    subtitle: () => 'Dùng mật khẩu mới để đăng nhập trang quản trị và app HP Media.',
  },
};

const ForgotPasswordPage = () => {
  const location = useLocation();
  const [initialEmail] = useState(() =>
    normalizeEmail((location.state as { email?: string } | null)?.email)
  );
  const [wide] = useState(isWideScreen);

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [resendAt, setResendAt] = useState(0);
  const [announcement, setAnnouncement] = useState('');
  const shownStep = useRef<Step>(step);

  // Đổi bước: đưa đầu thẻ (tiêu đề mới) vào tầm nhìn và báo cho trình đọc màn hình. So với bước
  // trước đó (không dùng cờ "lần đầu"): StrictMode chạy effect hai lần lúc mở trang.
  useEffect(() => {
    if (shownStep.current === step) return;
    shownStep.current = step;
    window.scrollTo({ top: 0 });
    setAnnouncement(
      step === 'done'
        ? 'Đã đổi mật khẩu.'
        : `Bước ${STEP_NUMBER[step]}/${TOTAL_STEPS}: ${STEP_COPY[step].title}.`
    );
  }, [step]);

  const loginState = email ? { email } : undefined;

  // Lần đầu mở trang: chỉ đặt con trỏ khi màn rộng. Sau mỗi lần bấm chuyển bước cũng vậy.
  const autoFocus = wide;

  let body: ReactNode;
  switch (step) {
    case 'email':
      body = (
        <StepEmail
          initialEmail={email}
          autoFocus={autoFocus}
          onSent={(sentTo) => {
            setEmail(sentTo);
            setCode('');
            setCodeError(null);
            setResendAt(Date.now() + RESEND_COOLDOWN_MS);
            setStep('code');
          }}
        />
      );
      break;
    case 'code':
      body = (
        <StepCode
          email={email}
          initialCode={code}
          initialCodeError={codeError}
          resendAt={resendAt}
          autoFocus={autoFocus}
          onResent={() => {
            setCodeError(null);
            setResendAt(Date.now() + RESEND_COOLDOWN_MS);
          }}
          onVerified={(verified) => {
            setCode(verified);
            setCodeError(null);
            setStep('password');
          }}
          onChangeEmail={() => {
            setCode('');
            setCodeError(null);
            setStep('email');
          }}
        />
      );
      break;
    case 'password':
      body = (
        <StepPassword
          email={email}
          code={code}
          autoFocus={autoFocus}
          onDone={() => setStep('done')}
          onCodeRejected={() => {
            setCodeError(FORGOT_MESSAGES.codeWrong);
            setStep('code');
          }}
        />
      );
      break;
    default:
      body = <StepDone email={email} autoFocus={autoFocus} />;
  }

  const inWizard = step !== 'done';

  return (
    <AuthLayout
      title={STEP_COPY[step].title}
      subtitle={STEP_COPY[step].subtitle(email)}
      step={inWizard ? { current: STEP_NUMBER[step], total: TOTAL_STEPS } : undefined}
      eyebrowAside={inWizard ? undefined : 'Hoàn tất'}
      backTo={
        inWizard ? { to: paths.login, label: 'Quay lại đăng nhập', state: loginState } : undefined
      }
      cardFooter={
        inWizard ? (
          <AuthSwitch
            prompt="Nhớ ra mật khẩu rồi?"
            to={paths.login}
            label="Đăng nhập"
            state={loginState}
          />
        ) : undefined
      }
    >
      <p className={styles.srOnly} aria-live="polite">
        {announcement}
      </p>
      {body}
    </AuthLayout>
  );
};

export default ForgotPasswordPage;
